// Helper function to escape XML special characters
function escapeXML(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// Helper function to convert scene title to slug
function slugify(text) {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-')           // Replace spaces with -
    .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
    .replace(/\-\-+/g, '-')         // Replace multiple - with single -
    .replace(/^-+/, '')             // Trim - from start of text
    .replace(/-+$/, '');            // Trim - from end of text
}

// Helper function to make URLs absolute
function makeAbsoluteUrl(url) {
  if (!url) return url;
  
  try {
    // Try to create URL object - if it succeeds, it's already absolute
    new URL(url);
    return url;
  } catch {
    // If URL creation fails, it's relative - make it absolute
    // Get the full path up to the last slash
    const basePath = window.location.pathname.substring(0, window.location.pathname.lastIndexOf('/') + 1);
    return new URL(url, window.location.origin + basePath).toString();
  }
}

// Helper function to create XML element with attributes
function createXMLElement(name, attributes = {}, children = '') {
  // Convert URLs in attributes to absolute URLs
  const processedAttributes = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (key.toLowerCase().includes('url')) {
      processedAttributes[key] = makeAbsoluteUrl(value);
    } else {
      processedAttributes[key] = value;
    }
  }
  
  const attrs = Object.entries(processedAttributes)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${key}="${escapeXML(String(value))}"`)
    .join(' ');
    
  if (children) {
    return `<${name}${attrs ? ' ' + attrs : ''}>${children}</${name}>`;
  }
  return `<${name}${attrs ? ' ' + attrs : ''}/>`;
}

// Generate scene XML
function generateSceneXML(scene) {
  // Create view element
  const view = createXMLElement('view', {
    hlookat: scene.view.hlookat,
    vlookat: scene.view.vlookat,
    fov: scene.view.fov,
    fovtype: "MFOV",
    fovmin: scene.view.fovmin,
    fovmax: scene.view.fovmax,
    limitview: "auto"
  });
  
  // Create image element
  // Try to get sphere images if available, otherwise fallback to cube
  let images = scene.images.filter(image => image.imageType === 'sphere');
  let imageElements;
  if (images.length > 0) {
    // get the highest quality sphere
    const highestQualitySphere = images.reduce((highest, current) => {
      return (current.tiledimagewidth > highest.tiledimagewidth) ? current : highest;
    }, images[0]);
    
    imageElements = createXMLElement('image', {}, 
      createXMLElement('sphere', {
        url: highestQualitySphere.url
      })
    );
  } else {
    images = scene.images.filter(image => image.imageType !== 'sphere');
    if (images.length === 0) {
        console.error('No images found for scene', scene.title);
        return;
    }
    imageElements = images.map(image => createXMLElement('image', {}, 
      createXMLElement('cube', {
        url: image.url
      })
    )).join('\n    ');
  }
  
  // Create scene element with all children
  return createXMLElement('scene', {
    id: scene.id,
    name: scene.name,
    title: scene.title,
    slug: slugify(scene.title),
    thumburl: scene.thumbUrl,
    burried: scene.burried || false
  }, `\n    ${view}\n    ${imageElements}\n  `);
}

import embedXML from './embed.xml';

// Main function to generate embed XML
export function generateEmbedXML(scenes) {
  // Validate scenes array
  if (!Array.isArray(scenes) || scenes.length === 0) {
    throw new Error('No scenes provided or invalid scenes array');
  }

  // Generate XML for each scene
  const scenesXML = scenes
    .map(scene => generateSceneXML(scene))
    .join('\n  ');
  
  // Create the complete XML with proper XML declaration
  const xml = embedXML.replace('<include url="embed-scenes.xml" />', scenesXML);
  return xml;
}

// Function to create a Blob URL for the generated XML
export function createEmbedXMLUrl(scenes) {
  try {
    const xml = generateEmbedXML(scenes);
    const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error generating embed XML:', error);
    throw error;
  }
}
